import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import classNames from 'classnames';
import { Page } from '@/components/Page/Page';
import { PatchType } from './components/PatchType/PatchType';
import {
  bundleUpchargesPayloadMapper,
  bundleUpchargesSort,
  isTheSameDescription,
  patchesPayloadMapper,
  useBundleUpcharges,
  useUpdateBundleUpcharges,
} from '@/services/bundles-upcharges/bundle-upcharges.service';
import { BundleUpcharge, BundleUpchargeUpdatePayload } from '@/types/bundles-upcharges.types';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';

import styles from './BundleUpcharges.module.css';

export const BundleUpcharges = () => {
  const { t } = useTranslation();
  const { data: bundleUpcharges, isLoading, isRefetching } = useBundleUpcharges();
  const { mutateAsync: updateBundleUpcharges, error } = useUpdateBundleUpcharges();
  const [bundleUpchargeSelected, setBundleUpchargeSelected] = useState<BundleUpcharge>({} as BundleUpcharge);
  const [descriptionAlreadyExists, setDescriptionAlreadyExists] = useState(false);

  const upcharges = useMemo(() => bundleUpchargesSort(bundleUpcharges), [bundleUpcharges]);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBundleUpchargeSelected((prev) => ({ ...prev, description: e.target.value }));
      const sameDescription = isTheSameDescription(upcharges, e.target.value, bundleUpchargeSelected?.type);
      setDescriptionAlreadyExists(Boolean(sameDescription));
    },
    [bundleUpchargeSelected?.type, upcharges],
  );

  const handleEdit = useCallback((bundleUpcharge: BundleUpcharge) => {
    setDescriptionAlreadyExists(false);
    setBundleUpchargeSelected(bundleUpcharge);
  }, []);

  const handleCancel = useCallback(() => {
    setBundleUpchargeSelected({} as BundleUpcharge);
    setDescriptionAlreadyExists(false);
  }, []);

  const handleToggleLock = useCallback(() => {
    setBundleUpchargeSelected((prev) => ({ ...prev, isLocked: !prev.isLocked }));
  }, []);

  const save = useCallback(
    async (bundleUpcharge: BundleUpcharge) => {
      const description = bundleUpchargeSelected?.description;
      if (!description || descriptionAlreadyExists) return;

      const patchesPayload = patchesPayloadMapper(bundleUpcharge);
      const payload: BundleUpchargeUpdatePayload = bundleUpchargesPayloadMapper(bundleUpcharge, patchesPayload);
      await updateBundleUpcharges([payload]);
      setBundleUpchargeSelected({} as BundleUpcharge);

      if (!isRefetching) {
        showToast({
          label: error ? t('error.updatingBundleUpcharge') : t('bundleUpcharges.updated'),
          toastType: error ? ToastTypes.Error : undefined,
          id: 'updateBundleUpcharges',
        });
      }
    },
    [bundleUpchargeSelected, descriptionAlreadyExists, error, isRefetching, t, updateBundleUpcharges],
  );

  return (
    <Page
      title={t('bundleUpcharges.title')}
      header={t('bundleUpcharges.header')}
      breadcrumb={{ label: t('breadcrumb.backToHome'), to: '/' }}
    >
      {isLoading && (
        <Box display="flex" flexDirection="column">
          <p className={classNames('skeleton', styles.loading)} />
          <p className={classNames('skeleton', styles.loading)} />
          <p className={classNames('skeleton', styles.loading)} />
          <p className={classNames('skeleton', styles.loading)} />
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap={3}>
        {upcharges?.map((bundleUpcharge) => (
          <PatchType
            key={bundleUpcharge.type}
            bundleUpcharges={bundleUpcharge}
            bundleUpchargeSelected={bundleUpchargeSelected}
            onEdit={() => handleEdit(bundleUpcharge)}
            onCancel={handleCancel}
            onToggleLock={handleToggleLock}
            onSave={save}
            onHandleDescriptionChange={handleDescriptionChange}
            descriptionAlreadyExists={descriptionAlreadyExists}
          />
        ))}
      </Box>
    </Page>
  );
};
